import { foundations } from '../foundations';
const { colors, shadows } = foundations;

export const Button = {
  baseStyle: {
    borderRadius: 'md',
    fontSize: '0.875rem',
    fontWeight: '600',
    lineHeight: '1em',
  },

  sizes: {
    xs: {
      padding: '.25rem .5rem',
      height: 'auto',
      fontSize: '0.75rem',
      textStyle: 'caption',
    },
    sm: {
      padding: '.5rem .75rem',
      fontSize: '0.875rem',
      textStyle: 'body',
      height: 'auto',
    },
    md: {
      padding: '.625rem .75rem',
      fontSize: '0.875rem',
      textStyle: 'body',
      height: 'auto',
    },
    lg: {
      padding: '.75rem 1rem',
      fontSize: '0.875rem',
      textStyle: 'body',
      height: 'auto',
    },
  },

  variants: {
    primarySolid: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
      color: '#231f20',
      bg: colors.button.primary.default[colorMode],
      boxShadow: shadows.sm,
      border: `1px solid ${colors.border.transparent[colorMode]}`,
      _hover: {
        bg: colors.button.primary.hover[colorMode],
      },
      _active: {
        bg: colors.button.primary.focus[colorMode],
      },
      _disabled: {
        bg: colors.button.disabled[colorMode],
        color: colors.text.tertiary[colorMode],
        _hover: {
          bg: colors.button.disabled[colorMode],
          color: colors.text.tertiary[colorMode],
        },
      },
    }),
    secondarySolid: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
      color: colors.text.base[colorMode],
      bg: colors.button.secondary.default[colorMode],
      boxShadow: shadows.sm,
      border: `1px solid ${colors.border.transparent[colorMode]}`,
      _hover: {
        bg: colors.button.secondary.hover[colorMode],
      },
      _active: {
        bg: colors.button.secondary.focus[colorMode],
      },
      _disabled: {
        bg: colors.button.disabled[colorMode],
        color: colors.text.tertiary[colorMode],
        _hover: {
          bg: colors.button.disabled[colorMode],
          color: colors.text.tertiary[colorMode],
        },
      },
    }),
    destructiveSolid: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
      color: '#ffffff',
      bg: colors.button.destroy.default,
      boxShadow: shadows.sm,
      border: `1px solid ${colors.border.transparent[colorMode]}`,
      _hover: {
        bg: colors.button.destroy.hover,
      },
      _active: {
        bg: colors.button.destroy.focus,
      },
      _disabled: {
        bg: colors.button.disabled[colorMode],
        color: colors.text.tertiary[colorMode],
        _hover: {
          bg: colors.button.disabled[colorMode],
          color: colors.text.tertiary[colorMode],
        },
      },
    }),
    primaryOutline: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
      color: colors.text.hyperlink[colorMode],
      bg: 'transparent',
      border: `1px solid ${colors.border.transparent[colorMode]}`,
      _hover: {
        bg: colors.interactive.hover[colorMode],
      },
      _active: {
        bg: colors.interactive.pressed[colorMode],
      },
      _disabled: {
        bg: 'transparent',
        color: colors.text.tertiary[colorMode],
      },
    }),
    secondaryOutline: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
      color: colors.text.base[colorMode],
      bg: 'transparent',
      border: `1px solid ${colors.border.transparent[colorMode]}`,
      _hover: {
        bg: colors.interactive.hover[colorMode],
      },
      _active: {
        bg: colors.interactive.pressed[colorMode],
      },
      _disabled: {
        bg: 'transparent',
        color: colors.text.tertiary[colorMode],
      },
    }),
    destructiveOutline: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
      color: colors.text.critical[colorMode],
      bg: 'transparent',
      border: `1px solid ${colors.border.transparent[colorMode]}`,
      _hover: {
        bg: colors.interactive.hover[colorMode],
      },
      _active: {
        bg: colors.interactive.pressed[colorMode],
      },
      _disabled: {
        bg: 'transparent',
        color: colors.text.tertiary[colorMode],
      },
    }),
    primaryGhosted: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
      color: colors.text.hyperlink[colorMode],
      bg: 'transparent',
      _hover: {
        bg: colors.interactive.hover[colorMode],
      },
      _active: {
        bg: colors.interactive.pressed[colorMode],
      },
      _disabled: {
        bg: 'transparent',
        color: colors.text.tertiary[colorMode],
      },
    }),
    secondaryGhosted: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
      color: colors.text.base[colorMode],
      bg: 'transparent',
      _hover: {
        bg: colors.interactive.hover[colorMode],
      },
      _active: {
        bg: colors.interactive.pressed[colorMode],
      },
      _disabled: {
        bg: 'transparent',
        color: colors.text.tertiary[colorMode],
      },
    }),
    destructiveGhosted: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
      color: colors.text.critical[colorMode],
      bg: 'transparent',
      _hover: {
        bg: colors.interactive.hover[colorMode],
      },
      _active: {
        bg: colors.interactive.pressed[colorMode],
      },
      _disabled: {
        bg: 'transparent',
        color: colors.text.tertiary[colorMode],
      },
    }),
    /**
     * variant below is for menu button that will mimic a select component
     * this is temporary until we have a real custom dropdown component
     */
    mimicSelectDropdown: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
      w: '100%',
      textAlign: 'left',
      variant: 'ghost',
      colorScheme: 'gray',
      border: '1px solid',
      borderColor: colors.border.form.default[colorMode],
      _hover: {
        bg: 'none',
        borderColor: colors.border.form.hover[colorMode],
        cursor: 'default',
      },
      _active: {
        bg: 'none',
        borderColor: colors.border.form.focus,
        boxShadow: `0 0 0 1px ${colors.border.form.focus}`,
      },
      _disabled: {
        bg: colors.background.form.disabled[colorMode],
        cursor: 'not-allowed',
        opacity: 0.4,
      },
      fontWeight: 400,
      py: '0.5rem',
      pr: '0.5rem',
    }),
  },
};
